import React from 'react'
import type { HTMLAttributes, ReactNode } from 'react'

import Spinner from '~/components/Common/Spinner'

import { classNames } from '~/utils/classNames'

type TileProps = HTMLAttributes<HTMLDivElement> & {
  error?: Error
}

export const Tile = ({ error, className, children, ...props }: TileProps) => {
  if (error) {
    return (
      <div className={classNames('tile', className)}>
        <div className="error-state">Error: {error.message}</div>
      </div>
    )
  }

  return (
    <article
      {...props}
      className={classNames(
        'px-4 py-6',
        props.onClick || props.onMouseDown ? 'cursor-pointer select-none duration-100 hover:bg-th-content-secondary' : '',
        className
      )}
    >
      {children}
    </article>
  )
}

type TileHeaderProps = {
  title?: ReactNode
  subtitle?: ReactNode
  item?: ReactNode
  loading?: boolean
  children?: ReactNode
  className?: string
}

export const TileHeader = ({ loading, title, subtitle, item, children, className }: TileHeaderProps) => {
  const childrenArray = React.Children.toArray(children)
  const titleComponent = childrenArray.find((child: any) => child?.type === TileHeader.Title) || title
  const subtitleComponent = childrenArray.find((child: any) => child?.type === TileHeader.Subtitle) || subtitle
  const itemComponent = childrenArray.find((child: any) => child?.type === TileHeader.Item) || item

  return (
    <header className={classNames('flex items-center justify-between gap-2 px-4', className)}>
      <div className="flex flex-col">
        <div className="flex items-center gap-2">
          {titleComponent}
          {loading && <Spinner className="h-4 w-4" />}
        </div>
        {subtitleComponent}
      </div>
      {itemComponent}
    </header>
  )
}

TileHeader.Title = ({ children, className }: { children: ReactNode; className?: string }) => {
  return <strong className={classNames('font-headline text-2xl font-bold tracking-wide text-th-text', className)}>{children}</strong>
}

TileHeader.Item = ({ children }: { children: ReactNode }) => {
  return <div className="flex items-center gap-2">{children}</div>
}

TileHeader.Subtitle = ({ children }: { children: ReactNode }) => {
  return <h3 className="text-sm font-bold text-th-text-secondary">{children}</h3>
}

type TileContentProps = {
  children: ReactNode
  as?: React.ElementType
  className?: string
  bordered?: boolean
}

export const TileContent = ({ children, as, className, bordered }: TileContentProps) => {
  const Component = as ?? 'div'
  return (
    <Component
      className={classNames(
        '[&>*]:mx-4',
        className,
        bordered ? '[&>hr]:border-th-border' : '[&>*]:border-th-translucent',
        Array.isArray(children) && children.length > 0 ? 'pt-3' : ''
      )}
    >
      {children}
    </Component>
  )
}

type TileListContentProps = {
  children: ReactNode
  limit?: number
  className?: string
  bordered?: boolean
}

export const TileListContent = ({ children, limit = 3, className, bordered }: TileListContentProps) => {
  if (!children) return null

  if (Array.isArray(children)) {
    return (
      <TileContent as="ul" className={className} bordered={bordered}>
        {children.slice(0, limit)}
      </TileContent>
    )
  }

  return <TileContent className={classNames(bordered ? 'border-t border-th-border' : '', className)}>{children}</TileContent>
}

interface TileListItemConfig extends HTMLAttributes<HTMLLIElement> {
  key: string | number
  title?: string
  subtitle?: string | string[]
}

export const TileListItem = (tileListItemConfig: TileListItemConfig) => {
  const childrenArray = React.Children.toArray(tileListItemConfig.children)
  const titleComponent = childrenArray.find((child: any) => child?.type === TileListItem.Title) || tileListItemConfig.title
  const subtitleComponent = childrenArray.find((child: any) => child?.type === TileListItem.Subtitle) || tileListItemConfig.subtitle
  const restOfChildren = childrenArray.filter((child: any) => child?.type !== TileListItem.Title && child?.type !== TileListItem.Subtitle)

  return (
    <>
      <hr />
      <li
        {...tileListItemConfig}
        className={classNames(
          '!mx-2 rounded-lg !px-2 py-2 text-sm',
          tileListItemConfig.onMouseDown || tileListItemConfig.onClick ? 'cursor-pointer select-none duration-100 hover:bg-th-content-secondary' : '',
          tileListItemConfig.className
        )}
      >
        {titleComponent}
        {subtitleComponent}
        {restOfChildren}
      </li>
    </>
  )
}

TileListItem.Title = ({ children, className }: { children: ReactNode; className?: string }) => {
  return <h4 className={classNames('font-bold', className)}>{children}</h4>
}

TileListItem.Subtitle = ({ children, className }: { children: ReactNode; className?: string }) => {
  return <p className={classNames('flex justify-between text-xs text-th-text-secondary', className)}>{children}</p>
}

type TileFooterProps = {
  children: ReactNode
  className?: string
} & HTMLAttributes<HTMLDivElement>

export const TileFooter = ({ children, className, ...props }: TileFooterProps) => {
  return (
    <footer
      className={classNames(
        'pt-2 text-center text-sm text-th-text-secondary',
        props.onMouseDown || props.onClick ? 'cursor-pointer select-none underline-offset-4 hover:underline' : '',
        className
      )}
      {...props}
    >
      {children}
    </footer>
  )
}
